import React from 'react'
import cx from 'classnames'
import { useFt } from 'hooks'
import type { ModalComponentProps } from 'modals'
import { useDevice } from 'device'
import { constants } from 'helpers'

import { Text } from 'components/dataDisplay'
import { WidthContainer } from 'components/layout'
import { PlainModal } from 'components/feedback'
import { CloseIconButton, SearchInput } from 'components/inputs'
import LogoLink from 'compositions/LogoLink/LogoLink'

import { useSearchModal } from './util'
import { Nav, ProductsGrid, TopRatedProducts } from './components'

import s from './SearchModal.scss'
import messages from './messages'


const SearchModal: React.FC<ModalComponentProps> = (props) => {
  const { name, closeModal } = props
  const handleCloseButtonClick = () => closeModal(true)

  const isCandleClubMoreBannersEnabled = useFt(constants.features.candleClubMoreBanners)

  const { isMobile } = useDevice()
  const { form, query, products, isFetching, error } = useSearchModal()

  let content

  if (error) {
    // TODO: "Server error" UI element
    content = error.message
  }
  /* User has not started to type a search request */
  else if (!query) {
    content = isMobile
      /* MOBILE: User see default recommendations of the first fragrances from Top Rated Products */
      ? (
        <TopRatedProducts
          className="pt-32"
          withBanner={isCandleClubMoreBannersEnabled}
          title={
            <Text
              className="mt-48"
              style="h5"
              message={messages.topRatedTitle}
              data-testid="topRatedLabel"
            />
          }
        />
      )
      /* DESKTOP: User see default links to the recommended categories */
      : <Nav className="mt-36" />
  }
  /* User typed the request, no matching products found */
  else if (query.length <= 2 || (!isFetching && !products?.length)) {
    /* User see "not found" text with default recommendations of the first fragrances from Top Rated Products */
    content = (
      <TopRatedProducts
        className={isMobile ? 'pt-24' : 'pt-32'}
        withBanner={isMobile && isCandleClubMoreBannersEnabled}
        title={
          <Text
            className={cx(isMobile ? 'mt-20' : 'mt-36', { 'text-center': !isMobile })}
            style="p2"
            message={messages.notFound}
            data-testid="notFoundBlock"
          />
        }
      />
    )
  }
  /* The user typed the request and found matching products */
  else {
    content = (
      <ProductsGrid
        className={isMobile ? 'pt-32' : 'pt-36'}
        products={products}
        isFetching={isFetching}
        data-testid="searchProducts"
        query={query}
      />
    )
  }

  return (
    <PlainModal
      type="fullscreen-top"
      withCloseButton={false}
      underMobileHeader={false}
      closeModal={closeModal}
      data-testid={name}
    >
      <WidthContainer className="w-full" ignore={isMobile}>
        <div className={cx(s.header, 'relative grid')}>
          <LogoLink className={s.logo} color="black" />
          <SearchInput
            className={s.searchInput}
            size={isMobile ? 30 : 34}
            field={form.fields.search}
            placeholder={messages.placeholder}
          />
          <CloseIconButton
            className={s.closeButton}
            onClick={handleCloseButtonClick}
          />
        </div>
        {content}
      </WidthContainer>
    </PlainModal>
  )
}


export default SearchModal
